import React, { useContext, useState } from 'react';
import { Button, Typography, useToast } from '@pw-tech/omni-ui';
import FooterPortal from '@/modules/common/Portals/FooterPortal/FooterPortal';
import { getNextStep } from '@/modules/common/utils';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { useRouter } from 'next/router';
import ScoreCalculation from '../components/ScoreCalculation/ScoreCalculation';
import Pass from './Pass/Pass';
import Fail from './Fail/Fail';
import Waiting from '../CeeFlow/Waiting/Waiting';
import ExamInProgress from './ExamInProgress/ExamInProgress';
import { Check, Clock, LaptopMinimal, NotebookPenIcon } from 'lucide-react';
import { RegistrationStatus } from '@/modules/common/enums';
import { DA_SYLLABUS, SOM_SYLLABUS } from '@/modules/common/constants';
const DirectAdmissionFlow = () => {
  const [showScoreCalculation, setShowScoreCalculation] = useState<boolean>(false);
  const {
    registrationDetails,
    submitRegisterForm,
    isFormSubmitting
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  const router = useRouter();
  const {
    toast: showToaster
  } = useToast();
  const handleShowScoreCalculation = () => {
    setShowScoreCalculation(!showScoreCalculation);
  };
  const handleMettlRedirect = (link: string) => {
    if (!link) {
      showToaster({
        message: 'Link not available',
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
      return;
    }
    window.open(link, '_blank');
  };
  const downloadSyllabus = () => {
    if (selectedCourseData?.batchCrmName.includes('SOT')) {
      window.open(DA_SYLLABUS, '_blank');
    } else if (selectedCourseData?.batchCrmName.includes('SOB')) {
      window.open(SOM_SYLLABUS, '_blank');
    } else {
      showToaster({
        message: 'Syllabus not available for this course',
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        }
      });
      return;
    }
    showToaster({
      message: 'Syllabus downloaded successfully',
      variant: 'success',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center'
      }
    });
  };
  const handleContinue = () => submitRegisterForm({
    step: getNextStep(router.query.step, registrationDetails?.step)
  }, true);
  const renderExamScreen = () => {
    const importantInstructions = ['The Fast Track Video Interview will be held online, so please ensure a stable internet connection.', 'Syllabus, Video Interview pattern, and mock test link are provided above - prepare well and give it your best.', 'For any issues during the Video Interview, feel free to contact our support team.'];
    return <article className="ml-2" data-sentry-component="renderExamScreen" data-sentry-source-file="index.tsx">
        <Typography variant="heading3" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          Fast Track Process
        </Typography>
        <Typography variant="regular" className="!text-[#757575] !text-sm mt-1 mb-3" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          You're eligible for Fast Track Process! Attempt the video interview below to proceed.
        </Typography>
        <div className="flex gap-2">
          <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            <NotebookPenIcon size={16} data-sentry-element="NotebookPenIcon" data-sentry-source-file="index.tsx" />
            {selectedCourseData?.metaData?.daTotalQuestions || 46} questions
          </Typography>
          <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            <Clock size={16} data-sentry-element="Clock" data-sentry-source-file="index.tsx" />
            {selectedCourseData?.metaData?.daTotalTime || 90} minutes
          </Typography>
          <Typography variant="regular" component="p" className="flex items-center gap-1 p-2 rounded-lg bg-[#F8F8F8] !text-xs" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            <LaptopMinimal size={16} data-sentry-element="LaptopMinimal" data-sentry-source-file="index.tsx" />
            {selectedCourseData?.metaData?.daExamMode || 'Online'}
          </Typography>
        </div>

        <div className="bg-[#EDFCF2] border-[#ADCFB7] border rounded-lg p-4 py-3 mt-4 flex flex-col">
          <Typography weight="semi-bold" className="!text-[#099250]" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            🎉 You're on the Fast Track!
          </Typography>
          <Typography variant="regular" className="!text-sm mt-1" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            No need to schedule - you can attempt the Video Interview right away.
          </Typography>
        </div>

        <div className="mt-4 p-2 sm:!p-4 bg-[#F8F8F8] rounded-lg">
          <div>
            <Typography variant="heading4" weight="semi-bold" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
              Important Instructions
            </Typography>
            <div className="pt-2 sm:!pt-[10px] flex flex-col gap-1">
              {importantInstructions.map((instruction, index) => <div key={index} className="flex gap-1">
                  <div className="flex items-center justify-center flex-shrink-0 w-5 h-5">
                    <Check size={20} color="#3D3D3D" />
                  </div>
                  <Typography className="!text-sm">{instruction}</Typography>
                </div>)}
            </div>
          </div>
          <div className="flex sm:!gap-4 pt-5">
            <Button type="button" variant="lowFocus" className="!text-[#196CDC]" onClick={() => handleMettlRedirect(registrationDetails?.mockTestUrl || '')} data-sentry-element="Button" data-sentry-source-file="index.tsx">
              Attempt Mock Test
            </Button>
            <Button type="button" variant="link" className="!bg-[#F8F8F8]" onClick={() => downloadSyllabus()} data-sentry-element="Button" data-sentry-source-file="index.tsx">
              Download Full Syllabus
            </Button>
          </div>
        </div>
        <div className="h-8" />
        <div className="py-2">
          <FooterPortal label="Start Video Interview" type="button" btnWrapperClass="relative sm:left-[31%]" onClick={() => handleMettlRedirect(registrationDetails?.testUrl ?? '')} data-sentry-element="FooterPortal" data-sentry-source-file="index.tsx" />
        </div>
      </article>;
  };
  const renderByStatus = () => {
    console.log('Registration Details in DA Flow: ', registrationDetails);
    switch (registrationDetails?.da_status) {
      // Exam in progress — resume exam
      case RegistrationStatus.da_started:
        return <ExamInProgress />;

      // Waiting for result
      case RegistrationStatus.da_completed:
        return <Waiting />;

      // Passed — show result + continue
      case RegistrationStatus.da_passed_lite:
      case RegistrationStatus.da_passed_excellence:
        return <article>
            <div className="flex flex-wrap justify-between gap-2">
              <Typography variant="heading3" weight="semi-bold">
                Fast Track Process Result
              </Typography>
            </div>

            <Pass />

            <div className="py-2">
              <FooterPortal label="Continue" type="button" btnWrapperClass="relative sm:left-[30%]" isFormSubmitting={isFormSubmitting} onClick={handleContinue} />
            </div>
          </article>;

      // Failed — show result + re-attempt
      case RegistrationStatus.da_failed:
        return <Fail />;

      // Default: registered / da_scheduled — show "attempt exam" screen
      default:
        return renderExamScreen();
    }
  };
  return <section data-sentry-component="DirectAdmissionFlow" data-sentry-source-file="index.tsx">{renderByStatus()}</section>;
};
export default DirectAdmissionFlow;